import type { IconConfig } from '@/menu/objectTypeRegistry';
import { getDataViewIconByComplexity } from '@/menu/objectTypeRegistry';
import type { TargetRouteSpec, MenuTargetContext } from '@/menu/menuTargetRegistry';

/** Verdier speilet mot `MenuTargetPresentationConstants` (backend). */
export const IconTypeKey = {
  Table: 'table',
  DataViewModel: 'data-view-model',
  DataViewSystem: 'data-view-system',
  Report: 'report',
  Module: 'module',
  TimeSheet: 'time-sheet',
  ExternalLink: 'external-link',
  Query: 'query',
  Default: 'default'
} as const;

export const BehaviorTypeKey = {
  TableBrowse: 'table-browse',
  DataViewBrowse: 'data-view-browse',
  ReportView: 'report-view',
  TripletexModule: 'tripletex-module',
  TimeSheetModule: 'time-sheet-module',
  RiskManagementModule: 'risk-management-module',
  ProjectPlanningModule: 'project-planning-module',
  DevOpsModule: 'devops-module',
  SnomedCtModule: 'snomedct-module',
  KodeverkModule: 'kodeverk-module',
  ExternalNavigate: 'external-navigate',
  QueryRun: 'query-run',
  Default: 'default'
} as const;

const defaultIcon: IconConfig = {
  icon: 'question-circle',
  color: 'rgb(var(--color-accent-default) / 1)',
  variant: 'fal'
};

const tableCellsHeaderLock: IconConfig = {
  icon: 'table-cells-header-lock',
  color: 'rgb(var(--color-accent-default) / 1)',
  variant: 'fad'
};

/** Felles visuelt uttrykk for eksterne moduler (inkludert DevOps) via backend `iconType = module`. */
const externalIntegrationIcon: IconConfig = {
  icon: 'globe',
  color: 'rgb(var(--color-accent-default) / 1)',
  variant: 'fal',
  layerIcons: [
    { icon: 'globe', variant: 'fal' },
    {
      icon: 'square',
      variant: 'fas',
      transform: 'shrink-3 down-4 right-4',
      color: 'rgb(var(--color-bg-surface) / 1)'
    },
    {
      icon: 'rectangle-api',
      variant: 'fal',
      transform: 'shrink-5 down-4 right-4',
    }
  ]
};

const iconByPresentationKey: Record<string, IconConfig> = {
  [IconTypeKey.Table]: tableCellsHeaderLock,
  [IconTypeKey.Report]: { icon: 'chart-line-up', color: 'rgb(var(--color-accent-default) / 1)', variant: 'fal' },
  [IconTypeKey.Module]: externalIntegrationIcon,
  [IconTypeKey.TimeSheet]: { icon: 'clock', color: 'rgb(var(--color-accent-default) / 1)', variant: 'fal' },
  [IconTypeKey.ExternalLink]: { icon: 'link', color: 'rgb(var(--color-accent-default) / 1)', variant: 'fal' },
  [IconTypeKey.Query]: defaultIcon,
  [IconTypeKey.Default]: defaultIcon
};

/**
 * Ikoner styres utelukkende av backend `iconType`.
 * Datavisning krever fortsatt systemgenerert-flagg (leveres fra API som eget ikon-nøkkel).
 */
export function getIconConfigForPresentation (iconType: string | null | undefined): IconConfig {
  const key = String(iconType || '').trim();
  if (key === IconTypeKey.DataViewModel) {
    return getDataViewIconByComplexity(false);
  }
  if (key === IconTypeKey.DataViewSystem) {
    return getDataViewIconByComplexity(true);
  }
  return iconByPresentationKey[key] ?? defaultIcon;
}

/**
 * Felles ikonutleding for ny app-shell — brukes av AppTabStrip, MenuColumnItem
 * og AppMenuPanel kolonne-header. Kun leaf-noder får ikon (presentation-ikon
 * basert på backend `iconType` / V3 `Icon`-feltet). Grupperinger (folders)
 * vises uten ikon — etiketten alene identifiserer kategorien.
 *
 * `color: currentColor` lar wrapperens Tailwind-klasse (typisk `text-accent`)
 * styre fargen så ikonene kontraster i både light- og dark-modus. Legacy
 * `rgb(var(--color-accent-default) / 1)` (navy #194378) blir usynlig mot dark `bg-surface`.
 */
export function getAppShellIconForNode (
  node: { isLeaf: boolean; icon?: string | null }
): IconConfig | null {
  if (!node.isLeaf) return null;
  const base = getIconConfigForPresentation(node.icon);
  return { ...base, color: 'currentColor' };
}

/** Rute for innholdsnavigasjon: enten kun publicId, eller projectSlug + publicId. */
type ViewRouteRule =
  | { scope: 'public-only'; routeName: string }
  | { scope: 'project-and-public'; routeName: string };

const viewRouteRuleByBehavior: Partial<Record<string, ViewRouteRule>> = {
  [BehaviorTypeKey.TableBrowse]: { scope: 'project-and-public', routeName: 'table-public' },
  [BehaviorTypeKey.DataViewBrowse]: { scope: 'public-only', routeName: 'data-view-public' },
  [BehaviorTypeKey.ReportView]: { scope: 'public-only', routeName: 'report-public' },
  [BehaviorTypeKey.TripletexModule]: { scope: 'project-and-public', routeName: 'TripletexModule' },
  [BehaviorTypeKey.DevOpsModule]: { scope: 'project-and-public', routeName: 'DevOpsModule' },
  [BehaviorTypeKey.SnomedCtModule]: { scope: 'project-and-public', routeName: 'SnomedCtModule' },
  [BehaviorTypeKey.KodeverkModule]: { scope: 'project-and-public', routeName: 'KodeverkModule' }
};

/** Moduler adressert av prosjektet alene — ruten tar kun projectSlug (publicId ignoreres). */
const projectOnlyModuleRouteByBehavior: Partial<Record<string, string>> = {
  [BehaviorTypeKey.TimeSheetModule]: 'TimeSheetModule',
  [BehaviorTypeKey.RiskManagementModule]: 'RiskManagementModule',
  [BehaviorTypeKey.ProjectPlanningModule]: 'ProjectPlanningModule'
};

export function buildViewRouteFromPresentation (
  behaviorType: string | null | undefined,
  targetPublicId: string,
  context: MenuTargetContext
): TargetRouteSpec | null {
  const bt = String(behaviorType || '').trim();
  const pid = String(targetPublicId || '').trim();
  const projectSlug = String(context.projectSlug || '').trim();
  if (!bt) return null;

  const projectOnlyRouteName = projectOnlyModuleRouteByBehavior[bt];
  if (projectOnlyRouteName) {
    if (!projectSlug) return null;
    return { name: projectOnlyRouteName, params: { projectSlug } };
  }

  if (!pid) return null;

  const rule = viewRouteRuleByBehavior[bt];
  if (!rule) return null;

  if (rule.scope === 'public-only') {
    return { name: rule.routeName, params: { publicId: pid } };
  }

  if (!projectSlug) return null;
  return { name: rule.routeName, params: { projectSlug, publicId: pid } };
}

export function canOpenSetupForPresentation (behaviorType: string | null | undefined): boolean {
  const bt = String(behaviorType || '').trim();
  return bt === BehaviorTypeKey.TableBrowse || bt === BehaviorTypeKey.DataViewBrowse;
}

const setupRouteByBehavior: Partial<Record<string, { routeName: string; idParam: 'tableKey' | 'viewId' }>> = {
  [BehaviorTypeKey.TableBrowse]: { routeName: 'TableSetupWithId', idParam: 'tableKey' },
  [BehaviorTypeKey.DataViewBrowse]: { routeName: 'DataViewDetail', idParam: 'viewId' }
};

export function buildSetupRouteFromPresentation (
  behaviorType: string | null | undefined,
  setupTargetId: string,
  context: MenuTargetContext
): TargetRouteSpec | null {
  const bt = String(behaviorType || '').trim();
  const id = String(setupTargetId || '').trim();
  const projectSlug = String(context.projectSlug || '').trim();
  if (!bt || !id || !projectSlug) return null;

  const spec = setupRouteByBehavior[bt];
  if (!spec) return null;

  return {
    name: spec.routeName,
    params: { projectSlug, [spec.idParam]: id }
  };
}

